import { Play, Clock, AlertCircle } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import { StatusBadge } from '@/components/pipeline/StatusBadge'
import { getErrorMessage } from '@/lib/errorMessages'
import type { Task } from '@/lib/types'

interface TaskCardProps {
  task: Task
  isSelected: boolean
  onClick: () => void
}

export function TaskCard({ task, isSelected, onClick }: TaskCardProps) {
  const canRun = task.status === 'idle' || task.status === 'complete' || task.status === 'error'

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-3 rounded-md border cursor-pointer group',
        'hover:border-slate-500',
        isSelected
          ? 'bg-slate-800 border-slate-500'
          : 'bg-slate-950/30 border-slate-800'
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm truncate flex-1">
          {task.name || 'Untitled Task'}
        </h4>
        {canRun && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              // Quick run action - would need API call here
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-teal-500/20 text-teal-400"
            title="Quick run"
          >
            <Play className="w-3 h-3" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 line-clamp-2 mb-3">
        {task.motivation?.slice(0, 80) || 'No motivation set'}
      </p>

      {task.status === 'error' && task.last_result?.error && (
        <div className="mb-3 p-2 bg-red-950/40 border border-red-900 rounded-md flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-300 leading-relaxed">
            {getErrorMessage(task.last_result.error)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-[auto_1fr] items-center gap-2">
        <StatusBadge status={task.status} size="sm" />
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-slate-500">
          {task.last_result && (
            <span className="whitespace-nowrap font-mono">
              {task.last_result.final_count || 0} results
            </span>
          )}
          {task.last_run_at && (
            <span className="flex items-center gap-1 whitespace-nowrap" title={new Date(task.last_run_at).toLocaleString()}>
              <Clock className="w-3 h-3" />
              {formatRelativeTime(task.last_run_at)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
